import { isValidElementType } from 'react-is'
import { Link } from 'react-router-dom'
import _ from 'lodash'
import classNames from 'classnames'
import md5 from 'md5'
import { ajax } from 'rxjs/ajax'
import NavIcon from '../../common/NavIcon'
import { globalAvail, tsgAccessAvail } from '../../common/utils'
import { getFrameworkVar, getFrameworkServiceUrl } from '../../../framework/states'
import { getDatadogRum } from '../../../framework/externals'
import history from '../../../framework/history'
import { stateRef } from '../../../framework/store'
import { jwtDecode as jwt_decode } from '../../../framework/jwt'
import { hasTsgSupport, getTsgToken, getCurrentTsgId, clearCurrentTsgId } from '../../../framework/tsg'
import { ADMIN_CLUSTER_URL_NAME, MGMT_MANAGE_ROUTE, MGMT_OVERVIEW_ROUTE, REQUEST_TIMEOUT, TSG_ID_NONE } from '../../../framework/constants'
import { __info, __isDebugOn } from '../../../init/debug'
import type { ElementType, ReactNode, MouseEvent } from 'react'
import type { AjaxResponse } from 'rxjs/ajax'
import type { ComponentOrLazyFactory, NavItem, TopLevelNavItem } from '../../../framework/microAppTypes'
import type { ConfigLocation, FeatureInfo } from '../../../framework/stateTypes'

export * from '../../../framework/loadingMask'
export * from './constants'
export { jwt_decode }

// avoid ts parse them to node version
const { setTimeout, clearTimeout, setInterval, clearInterval } = window

const preventDefault = (e: MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault()
}

// const JWT_IAT_TOLERANCE = 60000 // 60s

const validUserPreferenceAttributes = ['insights', 'explore', 'config']
let MAPBOX_TOKEN: string | undefined

export const getAdminClusterURL = (): string | undefined => getFrameworkServiceUrl(ADMIN_CLUSTER_URL_NAME)

export const getLoginServiceURL = (serviceUrl: string) => ({
  SERVICE_URL: `${getAdminClusterURL()}/${serviceUrl}`
})

export const clearUserSessionForLogout = () => {
  if (getCurrentTsgId() === TSG_ID_NONE) {
    clearCurrentTsgId()
    return
  }
  __info('clear fawkes session info and clear session storage')
  const sub = sessionStorage.getItem('currentTenantId') || localStorage.getItem('currentTenantId')
  if (sub) {
    localStorage.removeItem(`token-${sub}`)
    localStorage.removeItem(`token-exp-${sub}`)
    localStorage.removeItem('currentTenantId')
  }
  localStorage.removeItem('spk_user_theme') // TODO: remove when pref service integrated
  clearCurrentTsgId()
  sessionStorage.clear()
}

export const doLogout = () => {
  clearUserSessionForLogout()
  const logoutUrl = getLoginServiceURL('api/sso/v1/logout')['SERVICE_URL']
  window.location.href = logoutUrl
}

export const isTokenValid = ({ exp, iat }: { exp: number, iat: number }) => {
  const now = Date.now()
  return exp && iat &&
    now < exp * 1000
  // now + JWT_IAT_TOLERANCE > iat * 1000 // allow 60s local time diff
}

/**
 * Try getting tenantId from URL, or else from session
 * If none found, then we must ask user to establist a session
 * by providing a tenantId or launch us from Hub.
 */
export const doLogin = () => {
  let loginUrl = getLoginServiceURL('api/sso/v1/login')['SERVICE_URL']
  const urlParams = new URLSearchParams(window.location.search)
  const tenantId = urlParams.get('tenantId') || sessionStorage.getItem('currentTenantId') || localStorage.getItem('currentTenantId')
  if (tenantId) {
    const { origin, pathname } = window.location
    loginUrl += `?tenantId=${tenantId}`
    const redirectBack = urlParams.get('redirectBack') || getFrameworkVar<boolean>('redirect_back')
    if (redirectBack === true || redirectBack === 'true' || redirectBack === 'always') {
      const redirectBackUrl = encodeURIComponent(window.btoa(`${origin}${pathname}`))
      loginUrl += `&RelayState=${redirectBackUrl}`
    }
    window.location.href = loginUrl
  }
  else {
    // should never reach here but in case doLogin is called out of conext.
    throw new Error('No tenantId found!')
  }
}


/**
 * grid license key
 */
export { registerAGGridLicenseKey } from '../../../libs/base'

/**
 * mapbox license key
 * @param token
 */
export function registerMapboxToken(token: string | undefined) {
  MAPBOX_TOKEN = token
}

export function getMapboxToken() {
  return MAPBOX_TOKEN
}

let md5Salt: string = ''

export function initAuth(getUserResponse: any) {
  const headerValue = getUserResponse?.xhr?.getResponseHeader('x-csrf-salt')
  if (headerValue) {
    md5Salt = headerValue
    if ((window as any).Cypress) {
      (window as any).csrfSalt = md5Salt
    }
  }
  else {
    console.log('auth') // As little as possible
  }
}

export function getCSRFToken() {
  const now = Date.now()
  return `${now}:${md5(`${now}:${md5Salt}`)}`
}

export function getMd5Salt() {
  return md5Salt
}

export function getAuthHeaders() {
  const access_token = stateRef.current.auth?.access_token || getSession()

  // console.info('csrf token ', md5Salt);
  return {
    // salt should have been sent by Admin node already.
    'x-csrf-token': getCSRFToken(),
    // by this time, session value must be already set.
    'x-auth-jwt': access_token,
  }
}

export const getParam = (arg: string) => {
  const params = new URLSearchParams(window.location.search)
  if (params.has(arg)) {
    return params.get(arg) || undefined
  }
  return undefined
}

export const updateSession = ({ sub, token, exp }: { sub: string, token: string, exp: number }) => {
  if (token && token !== 'null') {
    localStorage.setItem(`token-${sub}`, token)
  }
  else {
    localStorage.deleteItem(`token-${sub}`)
  }
  if (exp) {
    const expStr = String(exp)
    localStorage.setItem(`token-exp-${sub}`, expStr)
    sessionStorage.setItem(`token-exp-${sub}`, expStr)
  }
  else {
    localStorage.deleteItem(`token-exp-${sub}`)
    sessionStorage.deleteItem(`token-exp-${sub}`)
  }
}

export const saveSession = ({ sub, token, exp }: { sub: string, token: string, exp: number }) => {
  sessionStorage.setItem('currentTenantId', sub)
  localStorage.setItem('currentTenantId', sub)
  updateSession({ sub, token, exp })
  setSessionAlert(exp)
  startSessionMonitor()
}

export const unsetSession = () => { // does not really clear the jwt
  sessionStorage.removeItem('currentTenantId')
  localStorage.removeItem('currentTenantId')
}

export const getSession = (sub?: string): string | undefined => {
  const { tsg_id, fawkes_tenant_id, access_token, access_token_decoded } = stateRef.current.auth || {}
  if (access_token && (!sub || sub === fawkes_tenant_id)) {
    if (access_token_decoded?.exp && access_token_decoded.exp * 1000 > Date.now()) {
      return access_token
    }
    console.error('access access_token from auth and its expired', access_token_decoded?.exp)
    getTsgToken(tsg_id).catch(_.noop) // try renew
    return undefined
  }
  const tenantId = sub || sessionStorage.getItem('currentTenantId')
  const token = tenantId && localStorage.getItem(`token-${tenantId}`)
  return (token !== 'null' && token) || undefined
}

const FIVE_MINS = 5 * 60 * 1000

let timer: number | undefined
let logouttimer: number | undefined
let inabout: number = 5
export const setSessionAlert = (expAt: number) => {
  if (hasTsgSupport()) {
    return // skip
  }
  const expiringIn = (expAt * 1000 - Date.now())
  let timeout = expiringIn - FIVE_MINS
  if (timeout < 0) {
    timeout = 0
    inabout = Math.floor(expiringIn / 60000)
  }
  clearTimeout(timer as number)
  timer = setTimeout(() => {
    clearTimeout(logouttimer as number)
    logouttimer = setTimeout(() => {
      window.location.hash = 'trylogin'
    }, inabout * 60000) as number
    // TODO REVIEW translation with parameters
    alert(`Your session will expire in about ${inabout} mins and you will be logged out. Please save your work before you are logged out.`)
  }, timeout) as number
}

let sessionTimer: number
export const startSessionMonitor = () => {
  if (hasTsgSupport()) {
    return // stop token sync across tab for iam token
  }
  clearInterval(sessionTimer)
  sessionTimer = setInterval(() => {
    const sub = sessionStorage.getItem('currentTenantId')
    if (!sub) {
      return
    }
    const localStoreExp = localStorage.getItem(`token-exp-${sub}`) || '0'
    const sessionStoreExp = sessionStorage.getItem(`token-exp-${sub}`) || '0'
    if (+localStoreExp && localStoreExp > sessionStoreExp) {
      // another window did a SAML Login - & now has updated token;
      // update the session timeout & alert. so user is not wrongly shown the session timeout alert.
      sessionStorage.setItem(`token-exp-${sub}`, localStoreExp)
      setSessionAlert(+localStoreExp)
    }
  }, 2500)
}

export function getFawkesTenantId(): string | undefined {
  let tenantId = getParam('tenantId')
  if (tenantId) {
    return tenantId
  }
  const { auth } = stateRef.current
  tenantId = auth?.fawkes_tenant_id ||
    sessionStorage.getItem('currentTenantId') ||
    localStorage.getItem('currentTenantId') || undefined
  return (tenantId !== 'null' && tenantId) || undefined
}

export function getRedirectURL() {
  const { auth, main } = stateRef.current
  const redirectUrl = getFrameworkVar<string>('redirect_url_override') ||
    main?.tenant?.cluster?.redirectURL || auth?.fawkes_paas_api_url
  if (redirectUrl) {
    return redirectUrl
  }
  const tenantId = getFawkesTenantId()
  const token = getSession(tenantId)
  if (token) {
    const { sub, exp, iat, redirectURL } = jwt_decode(token)
    if (isTokenValid({ exp, iat })) {
      console.log('Sending the token, with T=', sub)
      return redirectURL
    }
  }
  console.error('failed to get redirect url from state or fawkes jwt')
  return ''
}

export function getCustomServiceURL(serviceUrl: string) {
  let SERVICE_BASE_URL = getRedirectURL()
  if (!SERVICE_BASE_URL) return { SERVICE_URL: undefined }
  SERVICE_BASE_URL = SERVICE_BASE_URL.endsWith('/') ? SERVICE_BASE_URL.slice(0, SERVICE_BASE_URL.length - 1) : SERVICE_BASE_URL
  serviceUrl = serviceUrl.startsWith('/') ? serviceUrl.slice(1, serviceUrl.length) : serviceUrl
  return {
    SERVICE_URL: `${SERVICE_BASE_URL}/${serviceUrl}`
  }
}

export function getAdminServiceURL(adminUrl: string, extraParams?: Record<string, any>) {
  let urlParams = '?'
  if (!_.isEmpty(extraParams)) {
    for (const key in extraParams) {
      if (key && extraParams[key]) {
        urlParams = `${urlParams}&${key}=${extraParams[key]}`
      }
    }
  }
  urlParams = urlParams === '?' ? '' : urlParams
  return {
    LOGIN_SERVICE_URL: `${getAdminClusterURL()}/${adminUrl}${urlParams}`
  }
}

export function isInSession() {
  const tenantId = getFawkesTenantId()
  // see if we have token or not - with tenantId from url or from session storage
  const token = getSession(tenantId)
  return !tenantId && !token
}

export const getValidUserPreference = (userPreference: Record<string, any>) => {
  const preference = {} as Record<string, any>
  for (const key of Object.keys(userPreference)) {
    if (_.includes(validUserPreferenceAttributes, key)) {
      preference[key] = userPreference[key]
    }
  }
  return preference
}

export const checkIsFeatureEnabled = (featureList: FeatureInfo[] = [], featureKey = '') => {
  return featureList.some(feature => feature?.name === featureKey && feature.toggle === 1)
}

export function deepEquals(a: any, b: any, ca: any[] = [], cb: any[] = []): boolean {
  // Partially extracted from node-deeper and adapted to exclude comparison
  // checks for functions.
  // https://github.com/othiym23/node-deeper
  if (a === b) {
    return true
  }
  else if (typeof a === 'function' || typeof b === 'function') {
    // Assume all functions are equivalent
    // see https://github.com/mozilla-services/react-jsonschema-form/issues/255
    return true
  }
  else if (typeof a !== 'object' || typeof b !== 'object') {
    return false
  }
  else if (a === null || b === null) {
    return false
  }
  else if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime()
  }
  else if (a instanceof RegExp && b instanceof RegExp) {
    return (
      a.source === b.source &&
      a.global === b.global &&
      a.multiline === b.multiline &&
      a.lastIndex === b.lastIndex &&
      a.ignoreCase === b.ignoreCase
    )
  }
  else if (_.isArguments(a) || _.isArguments(b)) {
    if (!(_.isArguments(a) && _.isArguments(b))) {
      return false
    }
    const slice = Array.prototype.slice
    return deepEquals(slice.call(a), slice.call(b), ca, cb)
  }
  else {
    if (a.constructor !== b.constructor) {
      return false
    }

    const ka = Object.keys(a)
    const kb = Object.keys(b)
    // don't bother with stack acrobatics if there's nothing there
    if (ka.length === 0 && kb.length === 0) {
      return true
    }
    if (ka.length !== kb.length) {
      return false
    }

    let cal = ca.length
    while (cal--) {
      if (ca[cal] === a) {
        return cb[cal] === b
      }
    }
    ca.push(a)
    cb.push(b)

    ka.sort()
    kb.sort()
    for (let j = ka.length - 1; j >= 0; j--) {
      if (ka[j] !== kb[j]) {
        return false
      }
    }

    let key
    for (let k = ka.length - 1; k >= 0; k--) {
      key = ka[k]
      if (!deepEquals(a[key], b[key], ca, cb)) {
        return false
      }
    }

    ca.pop()
    cb.pop()

    return true
  }
}

export const findLeftNavTreeNodeWithKey = (routeData: any, key: any): any => {
  if (!routeData)
    return null

  let found = null
  for (let i = 0; i < routeData.length && !found; i++) {
    const node = routeData[i]
    if (key === node.key) {
      found = node
    }

    if (!found && node.children) {
      found = findLeftNavTreeNodeWithKey(node.children, key)
    }
  }
  return found
}

export const renderTitle = (title: ElementType | ReactNode | string | null | false | undefined) => {
  if (title === undefined) {
    return null
  }
  if (!_.isString(title) && isValidElementType(title)) {
    const Title = title as ElementType
    return <Title />
  }
  return title
}

export const convertToFirstLevelLeftNav = (routeData: TopLevelNavItem[], configLocation: any, licenseInfo: any, featuresInfo: any, isSideNavCollapsed: boolean) => {
  const treeData = []
  const paddingLeft = 0
  for (const treeNode of routeData) {
    let className = treeNode.className || ''
    const { key, title, path, icon, expandable, isLabel, hidden, overrideLeftNav } = treeNode
    if (isSideNavCollapsed) className += ' collapsed'
    if (isLabel && !hidden) {
      if (title && isValidRoute(treeNode)) {
        treeData.push({
          disabled: true,
          key,
          className: classNames('nav-tree-label', isSideNavCollapsed && 'collapsed'),
          title: !isSideNavCollapsed ? renderTitle(title) : null
        })
      }
    }
    else if (isValidRoute(treeNode) && !hidden && !(overrideLeftNav?.with === 'children')) {
      if ((!path || path === '#') && treeNode.component && !treeNode.children?.length) {
        console.error('invalid first level nav item without a valid path', treeNode)
        continue
      }
      const renderedTitle = renderTitle(title)
      const onClick = treeNode.children?.length && !(treeNode.component as ComponentOrLazyFactory)?.layout ? preventDefault : undefined
      const treeTitle = <Link className='spf-flex' to={path} data-nav-key={key || ''} onClick={onClick}>
        {renderedTitle}
        {!isSideNavCollapsed && <span className='spk-rc-tree-switcher-noop'></span>}
      </Link>
      const iconCmp = <NavIcon icon={icon} className='customize-icon' />
      const treeIcon = <Link style={{ paddingLeft }} to={path} onClick={onClick}>
        {iconCmp}
        {isSideNavCollapsed && <p className='sparky-left-nav-tooltip'>{renderedTitle}</p>}
      </Link>
      const treeClassName = expandable ? `pan-tree expandable ${className}` : `pan-tree non-expandable ${className}`
      const data = {
        key,
        className: treeClassName,
        icon: treeIcon,
        title: !isSideNavCollapsed ? treeTitle : ''
      }
      treeData.push(data)
    }
  }
  return treeData
}

export function getRBAPermission(rbaPath: string) {
  const rba = _.get(stateRef.current, `main.loggedInUser.rba.${rbaPath}`) || 'disable'
  return rba
}

export function isPermissionEnabled(permission: string) {
  return permission === 'enable'
}

export function isPermissionDisabled(permission: string) {
  return permission === 'disable'
}

export function isPermissionReadOnly(permission: string) {
  return permission === 'read-only'
}

export function getLocationPermission(objLocation: string, configLocation: ConfigLocation) {
  //check if the object location same as global location
  // let globalLocation = getPathValue(getStoreState(), 'main.configLocation');
  const locationName = configLocation ? configLocation.container ?
    configLocation.container.name : null :
    null
  return locationName === objLocation
}

export const recordLastAvailResult = (nav: any, isAvail: boolean, by: string) => {
  if (__isDebugOn() && nav.avail && !Object.isFrozen(nav)) {
    Object.defineProperties(nav, {
      lastIsAvail: {
        value: {
          result: isAvail,
          checkedBy: by,
        },
        writable: true,
      },
    })
  }
}

export const isValidRoute = (route: NavItem) => {
  const { auth, main } = stateRef.current
  const { configLocation, licenseInfo, featuresInfo } = main || {}
  const { instances, access, tsg_id } = auth || {}
  const objs = {
    ...configLocation,
    configLocation,
    location: history.location,
    licenseInfo,
    featuresInfo,
    instances,
    tsg_id,
    access,
    auth,
    main,
    navItem: route,
  }
  try {
    const noRBACheck = route.noRBACheck || hasTsgSupport()
    if (!noRBACheck && isPermissionDisabled(getRBAPermission(route.rbaPath))) {
      return false // TODO: should be removed once non-tsg is gone
    }
    const isAvail = !route.avail || Boolean(route.avail(objs, licenseInfo, featuresInfo))
    recordLastAvailResult(route, isAvail, 'utils')
    return isAvail && Boolean(route.bypassGlobalAvail || globalAvail()) &&
      Boolean(route.bypassTsgAvail || tsgAccessAvail())
  }
  catch (e) {
    console.error('failed to validate route due to exception', route, e)
    return false
  }
}

export const isLoginFailed = (): boolean => window.location.search.includes('loginFailed=true')

export function getUserLoggedInInfo() {
  const jwToken = window.location.hash.split('#_rjwt=')[1]
  const now = Date.now()
  if (jwToken) {
    // it was a redirect
    const { sub, exp, iat, redirectURL, supportAccountId } = jwt_decode(jwToken)
    if (isTokenValid({ exp, iat }) && // allow a little ahead in local time
      sub === getParam('tenantId')) { // token is not expired, issued earlier & match the tenant
      window.location.hash = '' // reset hash
      saveSession({ sub, token: jwToken, exp })
      return {
        isLoggedIn: true,
        tenant: {
          supportAccountId,
          cluster: {
            redirectURL
          }
        }
      }
    }
    if (iat && now < iat * 1000) {
      console.error(`JWT issue date is later than browser local time about ${iat * 1000 - now} ms`)
      // if (isTokenValid({ exp, iat: Date.now() / 1000 })) {
      //   alert('You local time is different from the server time more than the tolerance. Please check your local time settings.')
      //   const diff = iat * 1000 - Date.now()
      //   if (diff <= JWT_IAT_TOLERANCE) {
      //     setTimeout(() => window.location.reload(), diff)
      //   }
      //   else {
      //     window.location.reload()
      //   }
      //   return
      // }
    }
    return {
      isLoggedIn: false,
      expired: true,
    }
  }
  else {
    // got here after a refresh of the page or first time by navigating or copy-pasting url.
    // tenantId from url or sessionId.
    //if not from URL, get from localstorage and save it in sessionstorage
    const tenantId = getParam('tenantId') || localStorage.getItem('currentTenantId') || undefined
    // see if we have token or not - with tenantId from url or from session storage
    const token = getSession(tenantId)
    if (token) {
      const { sub, exp, iat, redirectURL, jti, supportAccountId } = jwt_decode(token)
      if (isTokenValid({ exp, iat })) {
        console.log('Sending the token, with T=', sub)
        saveSession({ sub, exp, token })
        return {
          isLoggedIn: true,
          jti,
          tenant: {
            supportAccountId,
            cluster: {
              redirectURL
            }
          }
        }
      }
      // else if (iat && now < iat * 1000) {
      //   console.error(`JTW issue date is later than browser local time about ${iat * 1000 - now} ms`)
      // }
    }
    // no redirected token or token expired or invalid.
    console.log('noSession since No TOKEN NO TENANT-ID.')
    return {
      isLoggedIn: false,
      noSession: !tenantId && !token
    }
  }
}

export const isInManageRoute = (path: string) =>
  path.startsWith(MGMT_OVERVIEW_ROUTE) || path.startsWith(MGMT_MANAGE_ROUTE)

export function sendErrorToDatadog(err: Error) {
  try {
    getDatadogRum()?.addError(err, {
      type: 'server_error',
    })
  }
  catch (e) {
  }
  throw err
}

export function getLoggedInUserPromise() {
  const { SERVICE_URL } = getCustomServiceURL('api/sso/v1/user')
  if (!SERVICE_URL) { // SERVICE address is not available yet.
    return Promise.resolve('NOT READY')
  }
  const promise = ajax({
    withCredentials: false,
    method: 'GET',
    responseType: 'json',
    timeout: REQUEST_TIMEOUT,
    url: SERVICE_URL,
    headers: getAuthHeaders()
  }).toPromise().catch(sendErrorToDatadog)
  return promise
}

export function getMigrationSatusPromise() {
  const promise = ajax({
    withCredentials: false,
    method: 'GET',
    responseType: 'json',
    timeout: REQUEST_TIMEOUT,
    url: `${getRedirectURL()}api/migration/status`,
    headers: getAuthHeaders()
  }).toPromise().catch(sendErrorToDatadog)
  return promise
}


export function getFeaturesPromise() {
  const { LOGIN_SERVICE_URL } = getAdminServiceURL('api/features')
  const promise = ajax({
    withCredentials: false,
    method: 'GET',
    responseType: 'json',
    timeout: REQUEST_TIMEOUT,
    url: LOGIN_SERVICE_URL,
    headers: getAuthHeaders()
  }).toPromise().catch(sendErrorToDatadog) as Promise<AjaxResponse>
  return promise
}


export function getTokenInfoPromise() {
  const { LOGIN_SERVICE_URL } = getAdminServiceURL('api/ui/tokens')
  const promise = ajax({
    withCredentials: false,
    method: 'GET',
    responseType: 'json',
    timeout: REQUEST_TIMEOUT,
    url: LOGIN_SERVICE_URL,
    headers: getAuthHeaders()
  }).toPromise().catch(sendErrorToDatadog) as Promise<AjaxResponse>
  return promise
}


export function getFqdnInfoPromise() {
  const { SERVICE_URL } = getCustomServiceURL('api/extensions/gpcs/gpFQDN')
  const promise = ajax({
    withCredentials: true,
    method: 'GET',
    responseType: 'json',
    timeout: REQUEST_TIMEOUT,
    url: SERVICE_URL,
    headers: getAuthHeaders()
  }).toPromise().catch(sendErrorToDatadog) as Promise<AjaxResponse>
  return promise
}


export function getLicenseInfoPromise() {
  const { SERVICE_URL } = getCustomServiceURL('api/extensions/gpcs/license')
  const promise = ajax({
    withCredentials: true,
    method: 'GET',
    responseType: 'json',
    url: SERVICE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: getAuthHeaders()
  }).toPromise().catch(sendErrorToDatadog) as Promise<AjaxResponse>
  return promise
}

export function getNotificationsPromise() {
  const { LOGIN_SERVICE_URL } = getAdminServiceURL(
    'api/v1/notifications', { readStatus: 'false' }
  )
  const promise = ajax({
    withCredentials: true,
    method: 'GET',
    responseType: 'json',
    url: LOGIN_SERVICE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: getAuthHeaders()
  }).toPromise().catch(sendErrorToDatadog) as Promise<AjaxResponse>
  return promise
}

export function getSubTenantInfoPromise() {
  const { SERVICE_URL } = getCustomServiceURL(
    'api/extensions/gpcs/get/get_tenant_subtenant_info'
  )

  const promise = ajax({
    withCredentials: false,
    method: 'GET',
    responseType: 'json',
    url: SERVICE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  }).toPromise().catch(sendErrorToDatadog) as Promise<AjaxResponse>
  return promise
}

export function getAuthInfoPromise() {
  const { LOGIN_SERVICE_URL } = getAdminServiceURL('api/xseer/auth')
  const promise = ajax({
    withCredentials: false,
    method: 'GET',
    responseType: 'json',
    url: LOGIN_SERVICE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  }).toPromise().catch(sendErrorToDatadog) as Promise<AjaxResponse>
  return promise
}

export function saveUserPreferencePromise(params: Record<string, any>) {
  const { SERVICE_URL } = getCustomServiceURL('api/preference')
  const userPreference = getValidUserPreference(params)
  const promise = ajax({
    withCredentials: false,
    method: 'POST',
    responseType: 'json',
    url: SERVICE_URL,
    timeout: REQUEST_TIMEOUT,
    body: { params: userPreference },
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  }).toPromise().catch(sendErrorToDatadog) as Promise<AjaxResponse>
  return promise
}
